import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableRow,
  CircularProgress, Alert, Stepper, Step, StepLabel, Breadcrumbs, Link, Button
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { stripGlSystemLabel, toChartOfAccountGlSystem, glSystemsEqual } from '../../constants/glSyncDocumentTypes';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
} from '../../theme/harmonateStyles';

const GLSyncMappingViewOnly = () => {
  const navigate = useNavigate();
  const [sourceGLAccounts, setSourceGLAccounts] = useState([]);
  const [targetGLAccounts, setTargetGLAccounts] = useState([]);
  const [mappingData, setMappingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { tenantId } = useParams();
  const location = useLocation();
  
  // Get sourceGL and destinationGL from navigation state
  const { sourceGL, destinationGL } = location.state || {};

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Comparison', 'Output'];

  // Function to fetch GL accounts for the specific systems
  const fetchGLAccountsForMapping = async () => {
    const token = localStorage.getItem('token');
    const storedTenantId = (localStorage.getItem('tenant_id') || tenantId)?.trim();

    if (!token || !storedTenantId) {
      setError('Authentication token or tenant ID not found');
      return [];
    }

    if (!sourceGL || !destinationGL) {
      setError('Source and destination GL systems are required');
      return [];
    }

    try {
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
      const url = new URL(`${base}conductor/cash-recon/chart-of-account/by-tenant-source-filters`);
      url.searchParams.set('tenant_id', storedTenantId);
      url.searchParams.set('system_type', 'GL_SYNC');
      url.searchParams.append('gl_system', toChartOfAccountGlSystem(sourceGL));
      url.searchParams.append('gl_system', toChartOfAccountGlSystem(destinationGL));

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let detail = `Failed to fetch GL accounts (${response.status})`;
        try {
          const errJson = await response.json();
          detail = errJson.detail || errJson.message || errJson.error || detail;
        } catch (_) {
          /* keep detail */
        }
        throw new Error(detail);
      }

      const raw = await response.json();
      const list = Array.isArray(raw) ? raw : [];
      return list.map((row) => ({
        ...row,
        glId: row.glId ?? row.ledgerAccountId ?? null,
      }));
    } catch (error) {
      console.error('Error fetching GL accounts for mapping:', error);
      setError(error.message);
      return [];
    }
  };

  // Function to fetch GL Link Mappings
  const fetchGLLinkMappings = async () => {
    const token = localStorage.getItem('token');
    const storedTenantId = (localStorage.getItem('tenant_integration') || tenantId)?.trim();

    if (!token || !storedTenantId) {
      setError('Authentication token or tenant ID not found');
      return [];
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/?tenant=${storedTenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch GL Link Mappings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching GL Link Mappings:', error);
      setError(error.message);
      return [];
    }
  };

  // Handle stepper navigation
  const handleStepClick = (stepIndex) => {
    if (stepIndex === 0) {
      // Navigate to comparison step
      navigate('/gl-sync/trial-balance-compare', {
        state: {
          sourceGL: sourceGL || 'Efront',
          destinationGL: destinationGL || 'Allvue',
          glSystem: stripGlSystemLabel(sourceGL || 'Efront') || sourceGL || 'Efront',
          activeStep: 0, // Set to comparison step
        },
      });
    } else if (stepIndex === 1) {
      // Navigate to output step
      navigate('/gl-sync/gl-mapping-results', {
        state: {
          sourceGL: sourceGL || 'Efront',
          destinationGL: destinationGL || 'Allvue',
          activeStep: 1, // Set to output step
        },
      });
    }
  };

  useEffect(() => {
    if (!sourceGL || !destinationGL) {
      setError('Source and destination GL systems are required');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch GL accounts for the specified systems
        const accountsData = await fetchGLAccountsForMapping();
        
        if (!accountsData || accountsData.length === 0) {
          throw new Error('No GL accounts found for the specified systems');
        }

        // Fetch GL Link Mappings
        const mappings = await fetchGLLinkMappings();

        // Since the new API doesn't have glSourceTarget field, we need to determine source/target based on glSystem
        // Note: API returns "Allvue"/"Efront" but we have "ALLVUE"/"EFRONT" from mapping-headers, so we need case-insensitive comparison
        const sourceAccounts = accountsData.filter((account) =>
          glSystemsEqual(account.glSystem, sourceGL)
        );
        const targetAccounts = accountsData.filter((account) =>
          glSystemsEqual(account.glSystem, destinationGL)
        );

        setSourceGLAccounts(sourceAccounts);
        setTargetGLAccounts(targetAccounts);

        // Create a map of source GL codes to their mappings for quick lookup
        const mappingsMap = new Map();
        mappings.forEach(mapping => {
          if (mapping.from_gl_code) {
            mappingsMap.set(mapping.from_gl_code, mapping);
          }
        });

        // Initialize mapping data with source accounts and apply existing mappings (view-only)
        const initialMappingData = sourceAccounts.map((account, index) => {
          const mapping = mappingsMap.get(account.glCode);
          const destinationAccount = mapping ?
            targetAccounts.find(t => t.glCode === mapping.to_gl_code) : null;

          return {
            glId: account.glId,
            glAccount: account.glAccount,
            glCode: account.glCode,
            glDescription: account.glDescription,
            glSystem: account.glSystem,
            destinationField: destinationAccount ?
              `${destinationAccount.glCode} - ${destinationAccount.glDescription}` :
              '-- No Mapping --',
            destinationGlCode: destinationAccount ? destinationAccount.glCode : null,
            highlighted: index % 3 === 1,
            mappingId: mapping ? mapping.id : null
          };
        });

        setMappingData(initialMappingData);
        setError(null);
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError(err.message || 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, sourceGL, destinationGL]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body1" sx={{ ml: 2, color: ht.white }}>Loading mapping data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={harmonatePageSx}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)} sx={harmonateProceedButtonSx}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={harmonatePageSx}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: ht.white }} />}
          sx={{ mb: 2, '& .MuiBreadcrumbs-li': { color: ht.white }, '& a': { color: ht.white } }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ht.white }}
          >
            <HomeIcon fontSize="small" />
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/gl-sync')}
            sx={{ cursor: 'pointer', color: ht.white }}
          >
            GL Sync
          </Link>
          <Typography sx={{ color: ht.white }}>Mapping View</Typography>
        </Breadcrumbs>
        
        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ color: '#000000', borderColor: '#ddd' }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            GL Mapping View
          </Typography>
        </Box> */}
        
        {/* <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Mapping Instance ID: {mappingInstanceId} (View Only)
        </Typography> */}
      </Box>

      {/* Stepper */}
      <Paper elevation={0} sx={{ ...harmonateCardSx, p: 3, mb: 3, bgcolor: ht.white }}>
        <Stepper 
          activeStep={activeStep}
          sx={{ 
            mb: 3,
            '& .MuiStepLabel-label': { 
              fontSize: '0.875rem',
              color: ht.text,
              '&.Mui-active, &.Mui-completed': {
                fontWeight: 600,
                color: ht.text,
              }
            },
            '& .MuiStepIcon-root.Mui-active': { color: ht.blue },
            '& .MuiStepIcon-root.Mui-completed': { color: ht.green },
            '& .MuiStepConnector-line': {
              marginLeft: 1,
              marginRight: 1,
              borderColor: ht.line,
            }
          }}
        >
          {steps.map((label, index) => (
            <Step 
              key={label}
              onClick={() => handleStepClick(index)}
              sx={{ 
                cursor: 'pointer',
                '& .MuiStepLabel-root': {
                  padding: '0 8px'
                }
              }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Mapping Table (View Only) */}
      <Paper elevation={0} sx={{ ...harmonateCardSx, p: 3, bgcolor: ht.white }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: ht.text }}>
          GL Mapping View
        </Typography>
        <Typography variant="body2" sx={{ color: ht.text, mb: 3 }}>
          View existing mappings between {sourceGL}_GL and {destinationGL}_GL GL accounts
        </Typography>

        <TableContainer>
          <Table>
            <TableBody>
              {mappingData.map((row, index) => (
                <TableRow 
                  key={row.glId}
                  sx={{
                    backgroundColor: row.highlighted ? ht.listBg : 'inherit',
                    '&:hover': {
                      backgroundColor: ht.secondaryTab,
                    },
                  }}
                >
                  <TableCell sx={{ width: '40%', fontWeight: 500, borderColor: ht.line }}>
                    <Typography variant="subtitle2">
                      {row.glAccount}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: '10%', textAlign: 'center', borderColor: ht.line }}>
                    <Typography variant="h6" sx={{ color: ht.green }}>
                      →
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ 
                    width: '50%', 
                    color: row.destinationField === '-- No Mapping --' ? ht.fadedText : ht.text,
                    fontStyle: row.destinationField === '-- No Mapping --' ? 'italic' : 'normal',
                    borderColor: ht.line,
                  }}>
                    {row.destinationField}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default GLSyncMappingViewOnly;
